import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

/** Web engines offered above the local results; all open in a new tab. */
const WEB_ENGINES = [
  { name: 'Google', url: (q) => 'https://www.google.com/search?q=' + encodeURIComponent(q) },
  { name: 'DuckDuckGo', url: (q) => 'https://duckduckgo.com/?q=' + encodeURIComponent(q) },
  { name: 'Bing', url: (q) => 'https://www.bing.com/search?q=' + encodeURIComponent(q) },
  { name: 'Brave', url: (q) => 'https://search.brave.com/search?q=' + encodeURIComponent(q) },
];

/**
 * Internal search: matches the visitor's query against this site's own
 * pages — titles first, then page content (the JSX/HTML strings the API
 * delivers, stripped to plain text). No external calls; the query has
 * already been logged by the nav search handler.
 *
 * Ranking: title phrase > title word > content phrase > content word.
 * A "search the web" DuckDuckGo button is always offered underneath.
 */

const stripMarkup = (jsx) =>
  String(jsx || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\{[^}]*\}/g, ' ')          // JSX expression bindings
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const pageLabel = (page) => (page.DT_Setting === 'Index' ? 'Home' : page.DT_Setting);

const tokenize = (text) => text.toLowerCase().split(/[^a-z0-9']+/).filter((t) => t.length >= 2);

/**
 * Match query words against a token list (exact or prefix only).
 * matchedTokens are the document's actual words, used for snippets and
 * highlighting.
 */
function matchWords(queryWords, tokens) {
  const matchedTokens = new Set();
  let exact = 0;
  for (const w of queryWords) {
    for (const t of tokens) {
      if (t === w || t.startsWith(w)) { exact++; matchedTokens.add(t); break; }
    }
  }
  return { exact, matchedTokens: [...matchedTokens] };
}

function buildSnippet(text, phrase, tokens) {
  const lower = text.toLowerCase();
  let idx = phrase ? lower.indexOf(phrase) : -1;
  let matchLen = phrase ? phrase.length : 0;
  if (idx === -1) {
    for (const t of tokens) {
      idx = lower.indexOf(t);
      if (idx !== -1) { matchLen = t.length; break; }
    }
  }
  if (idx === -1) return null;
  const start = Math.max(0, idx - 70);
  const end = Math.min(text.length, idx + matchLen + 90);
  return (start > 0 ? '…' : '') + text.slice(start, end).trim() + (end < text.length ? '…' : '');
}

function Highlight({ text, words }) {
  if (!text || words.length === 0) return <>{text}</>;
  const re = new RegExp('(' + words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')', 'ig');
  const parts = String(text).split(re);
  const lowerWords = words.map((w) => w.toLowerCase());
  return (
    <>
      {parts.map((p, i) =>
        lowerWords.includes(p.toLowerCase())
          ? <mark key={i} className="aw-search-mark">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

const InternalSearchResults = ({ query }) => {
  const pages = useSelector((state) => state.domainData.data?.pages) || [];

  const { results, words } = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return { results: [], words: [] };
    const wordList = tokenize(q);
    if (wordList.length === 0) return { results: [], words: [] };
    const scored = [];
    const highlightTokens = new Set(wordList);

    pages.forEach((page) => {
      const url = (page.DT_Setting_URL || '').toLowerCase();
      if (!url || url === 'login') return;
      const title = pageLabel(page) || '';
      const titleLower = title.toLowerCase();
      const content = stripMarkup(page.DT_Content_EN);
      const contentLower = content.toLowerCase();

      // Exact-phrase tiers first, then per-word exact/prefix matches.
      let score = 0;
      let snippetTokens = wordList;

      const titleMatch = matchWords(wordList, tokenize(titleLower));
      const contentMatch = matchWords(wordList, tokenize(contentLower));

      if (titleLower.includes(q)) score = 100;
      else if (titleMatch.exact > 0) score = 70;
      else if (contentLower.includes(q)) score = 40;
      else if (contentMatch.exact > 0) score = 25;
      if (score === 0) return;

      [...titleMatch.matchedTokens, ...contentMatch.matchedTokens].forEach((t) => highlightTokens.add(t));
      if (contentMatch.matchedTokens.length > 0) snippetTokens = contentMatch.matchedTokens;

      scored.push({
        url: page.DT_Setting_URL,
        title,
        score,
        snippet: score < 55 ? buildSnippet(content, contentLower.includes(q) ? q : null, snippetTokens) : null,
      });
    });

    scored.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
    return { results: scored.slice(0, 20), words: [...highlightTokens] };
  }, [pages, query]);

  if (!query || !query.trim()) return null;

  return (
    <div className="aw-websearch">
      <div className="aw-websearch-engines">
        <span className="aw-websearch-engines-label">Search the web for &ldquo;{query}&rdquo;:</span>
        {WEB_ENGINES.map((e) => (
          <a
            key={e.name}
            className="aw-websearch-ddg-btn aw-websearch-engine"
            href={e.url(query)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {e.name}
          </a>
        ))}
      </div>

      <h2 className="aw-websearch-heading">Results from this site</h2>
      {results.length === 0 ? (
        <p className="aw-websearch-status">No results found on our pages.</p>
      ) : (
        <ol className="aw-websearch-list">
          {results.map((r) => (
            <li key={r.url} className="aw-websearch-item">
              <Link to={'/' + (r.url === 'index' ? '' : r.url)} className="aw-websearch-title">
                <Highlight text={r.title} words={words.length ? words : [query.trim()]} />
              </Link>
              {r.snippet && (
                <p className="aw-websearch-snippet">
                  <Highlight text={r.snippet} words={words.length ? words : [query.trim()]} />
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default InternalSearchResults;
